import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, fmtMoney, CURRENCY_META } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Send as SendIcon, Loader2 } from "lucide-react";

export default function Send() {
  const { t } = useI18n();
  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [fromId, setFromId] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/accounts"), api.get("/currencies")]);
    setAccounts(a.data.filter((x) => x.status !== "closed"));
    setCurrencies(c.data);
  };
  useEffect(() => { load(); }, []);

  const curMap = Object.fromEntries(currencies.map((c) => [c.code, c]));

  const submit = async () => {
    if (!fromId) return toast.error(t("send.noFrom"));
    if (!toNumber.trim()) return toast.error(t("send.noTo"));
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error(t("tr.badAmount"));
    setBusy(true);
    try {
      await api.post("/operations/send", { from_account_id: fromId, to_account_number: toNumber.trim(), amount: amt, description: note });
      toast.success(t("send.done"));
      setToNumber(""); setAmount(""); setNote("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3"><SendIcon className="text-[#00E5FF]" /> {t("send.title")}</h1>
        <p className="text-zinc-500 text-sm mt-1">{t("send.sub")}</p>
      </div>

      <div className="ob-card rounded-md p-7 space-y-5">
        <div>
          <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("send.from")}</label>
          <select value={fromId} onChange={(e) => setFromId(e.target.value)} data-testid="send-from-select"
            className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white focus:border-[#00E5FF] outline-none">
            <option value="">{t("send.pickFrom")}</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{`${CURRENCY_META[a.currency_code]?.flag || ""} ${a.account_number} · ${fmtMoney(a.available_balance, curMap[a.currency_code]?.decimals || 2)}`}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("send.toNumber")}</label>
          <input value={toNumber} onChange={(e) => setToNumber(e.target.value)} dir="ltr" placeholder="OB-1000002-USD" data-testid="send-to-input"
            className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white font-mono-ob text-end focus:border-[#00E5FF] outline-none" />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("common.amount")}</label>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} dir="ltr" placeholder="0.00" data-testid="send-amount-input"
            className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white text-end focus:border-[#00E5FF] outline-none" />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("send.note")}</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("send.notePh")} data-testid="send-note-input"
            className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white focus:border-[#00E5FF] outline-none" />
        </div>
        <button onClick={submit} disabled={busy} data-testid="send-submit-button"
          className="w-full bg-[#00E5FF] text-black font-bold py-3 rounded-md hover:bg-[#33EBFF] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={18} className="animate-spin" />} {t("send.btn")}
        </button>
        <p className="text-xs text-zinc-600 text-center">{t("send.crossNote")}</p>
      </div>
    </div>
  );
}
